import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  inject,
} from '@angular/core';
import { formatAmountWithCode } from '@app/helpers/formatCurrency.helper';
import { boolBadge, boolToKey } from '@app/helpers/status.helper';
import type { UserAccount } from '@app/models/account.model';
import {
  DecisionBadgeGroup,
  DecisionBadgeItem,
} from '@app/models/status.model';
import { DecisionBadgeListComponent } from '@app/ui-components/decision-badge-list/decision-badge-list.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

type InvestorCriterionId =
  | 'minGrossIncome'
  | 'atLeastOneYerExp'
  | 'experienced'
  | 'validStatements'
  | 'q1OwnsTransferableSecurities'
  | 'q2OwnsCrowdfundingAssetsOrLoans'
  | 'q3HasCrowdfundingInvestments'
  | 'q4OwnsTransferableSecurities';

type GroupId = 'investorRating' | 'investorStatement';
type InvestorItem = DecisionBadgeItem<InvestorCriterionId>;
type IncomeRowView = {
  id: string;
  netIncome: number | null;
  regularIncome: boolean | null;
};

@Component({
  selector: 'app-qualification-tab',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    DecisionBadgeListComponent,
  ],
  templateUrl: './qualification-tab.component.html',
  styleUrl: './qualification-tab.component.scss',
})
export class QualificationTabComponent implements OnChanges {
  @Input() user: UserAccount | null = null;

  incomeRows: ReadonlyArray<IncomeRowView> = [];
  readonly boolBadge = boolBadge;
  readonly boolToKey = boolToKey;

  private readonly translate = inject(TranslateService);

  ngOnChanges(_: SimpleChanges): void {
    this.hydrateFromUser();
  }

  get isProfessionalClient(): boolean {
    return this.user?.represents === 2;
  }

  get isQualificationNotFilled(): boolean {
    return this.user?.stepInvestorQualification !== true;
  }

  get badgeGroups(): ReadonlyArray<
    DecisionBadgeGroup<GroupId, InvestorCriterionId>
  > {
    return [
      {
        groupId: 'investorRating',
        title: 'general.investmentQualification',
        completedAt:
          this.user?.investorQualificationLastDate ??
          this.user?.notQualifiedConfirmAccuracyOfDeclarations ??
          this.user?.notQualifiedTestStartDate ??
          null,
        items: [
          {
            id: 'minGrossIncome',
            labelKey:
              'registrationWizard.investorRatingModal.experienceDetermination.minGrossIncome',
            value: this.user?.question1_60kor100kYearlyPortfolio ?? null,
          },
          {
            id: 'atLeastOneYerExp',
            labelKey:
              'registrationWizard.investorRatingModal.experienceDetermination.atLeastOneYerExp',
            value:
              this.user?.question2_YearinLeadLeadRoleWhere2mNetIncomeOrProf ??
              null,
          },
          {
            id: 'experienced',
            labelKey:
              'registrationWizard.investorRatingModal.experienceDetermination.experienced',
            value:
              this.user
                ?.question3_HasSignificantMarketActivityFor4QuarterTenTransaction ??
              null,
          },
        ] as ReadonlyArray<InvestorItem>,
      },
      {
        groupId: 'investorStatement',
        title: 'general.statement',
        items: [
          {
            id: 'validStatements',
            labelKey: 'general.validStatements',
            value: !!this.user?.notQualifiedConfirmAccuracyOfDeclarations,
          },
          {
            id: 'q1OwnsTransferableSecurities',
            labelKey:
              'registrationWizard.investmentQualificationTest.investorStatementForm.q1OwnsTransferableSecurities',
            value: this.user?.notQualifiedQ1ownsTransferableSecurities ?? null,
          },
          {
            id: 'q2OwnsCrowdfundingAssetsOrLoans',
            labelKey:
              'registrationWizard.investmentQualificationTest.investorStatementForm.q2OwnsCrowdfundingAssetsOrLoans',
            value:
              this.user?.notQualifiedQ2ownsCrowdfundingAssetsOrLoans ?? null,
          },
          {
            id: 'q3HasCrowdfundingInvestments',
            labelKey:
              'registrationWizard.investmentQualificationTest.investorStatementForm.q3HasCrowdfundingInvestments',
            value: this.user?.notQualifiedQ4hasCrowdfundingInvestments ?? null,
          },
          {
            id: 'q4OwnsTransferableSecurities',
            labelKey:
              'registrationWizard.investmentQualificationTest.investorStatementForm.q4OwnsTransferableSecurities',
            value: this.user?.notQualifiedQ3hasTransferableSecurities ?? null,
          },
        ] as ReadonlyArray<InvestorItem>,
      },
    ] as const;
  }

  private hydrateFromUser(): void {
    const incomes = this.user?.notQualifiedUserIncomes?.incomes ?? [];
    this.incomeRows = incomes.map((income, index) => ({
      id: `inc_${index + 1}`,
      netIncome: income.amount ?? null,
      regularIncome: income.regulary ?? null,
    }));
  }

  get netWealthAfterLoss(): number | null {
    const netWorth = this.user?.notQualifiedUserNetWorth;
    return typeof netWorth === 'number' ? netWorth * 0.9 : null;
  }

  formatCurrency(value: number | null): string {
    return formatAmountWithCode(
      value,
      this.user?.currencyCode ?? '-',
      this.translate.currentLang
    );
  }
}
